/**
 * Semantic asset-role classification for brand skinning.
 *
 * Replaces the old blind hero-image rotation: every image occurrence is
 * classified into a role, and only PHOTO slots may receive client photos —
 * icons, badges, social glyphs, avatars, and decorative art keep their
 * (neutralized) slots instead of becoming absurd hero photographs.
 *
 * Signals are the stable CodeStitch conventions verified across the 5,916
 * section corpus: `cs-picture` marks content photos (2,584 files);
 * `cs-icon`/`cs-icon-wrapper`/`aria-hidden`/`.svg`/`/assets/svgs/` mark
 * decorative icons; `cs-logo` marks logos; `cs-floater`/`cs-background`
 * mark decoration.
 *
 * Worker-safe: pure functions over already-parsed DOM elements; no imports
 * beyond shared modules.
 */

export const ASSET_ROLES = Object.freeze({
  LOGO: 'logo',
  MAP_EMBED: 'map-embed',
  ICON: 'icon',
  SOCIAL_ICON: 'social-icon',
  BADGE: 'badge',
  AVATAR: 'avatar',
  BACKGROUND: 'background',
  PHOTO: 'photo'
});

const RASTER_EXT_RE = /\.(jpe?g|png|webp|avif|gif|bmp)(\?|#|$)/i;
const SVG_EXT_RE = /\.svg(\?|#|$)/i;
const SOCIAL_TOKEN_RE =
  /(facebook|instagram|linkedin|twitter|x-logo|youtube|yelp|tiktok|vimeo|pinterest)/i;
const BADGE_TOKEN_RE = /(badge|award|stars|rating|review|bbb|guarantee|certified|accredited|seal)/i;
const AVATAR_TOKEN_RE = /(profile|avatar|headshot)/i;
const LOGO_TOKEN_RE = /(^|[^a-z0-9])logo([^a-z0-9]|$)/i;
const MAP_HOST_RE =
  /(google\.[a-z.]+\/maps|maps\.app\.goo\.gl|goo\.gl\/maps|maps\.apple\.com|waze\.com)/i;

export function isRasterUrl(url) {
  return RASTER_EXT_RE.test(String(url || ''));
}

export function isSvgUrl(url) {
  return SVG_EXT_RE.test(String(url || ''));
}

export function isMapEmbedUrl(url) {
  return MAP_HOST_RE.test(String(url || ''));
}

function attrNum(el, name) {
  const value = Number.parseFloat(el.getAttribute?.(name) || '');
  return Number.isFinite(value) ? value : null;
}

function classTokens(el) {
  return String(el.getAttribute?.('class') || '').toLowerCase();
}

function hasAncestorClass(el, token, maxDepth = 4) {
  let node = el.parentNode;
  for (let depth = 0; node && depth < maxDepth; depth += 1) {
    if (classTokens(node).includes(token)) return true;
    node = node.parentNode;
  }
  return false;
}

function closestCard(el, maxDepth = 6) {
  let node = el.parentNode;
  for (let depth = 0; node && depth < maxDepth; depth += 1) {
    const cls = classTokens(node);
    if (cls.includes('cs-item') || cls.includes('cs-review') || cls.includes('testimonial'))
      return node;
    node = node.parentNode;
  }
  return null;
}

/**
 * Classify an <img> (or <source>-bearing group's representative <img>).
 *
 * @param {Element} img
 * @param {{ src?: string, isLogo?: (img: Element, src: string) => boolean }} [opts]
 *   `isLogo` lets the caller supply profile-aware logo matching (source
 *   profile logoSrc/logoVariants); the class-based `cs-logo` signal is
 *   checked here regardless.
 * @returns {string} one of ASSET_ROLES
 */
export function classifyImageRole(img, opts = {}) {
  const src = String(opts.src ?? img.getAttribute?.('src') ?? '');
  const cls = classTokens(img);
  const alt = String(img.getAttribute?.('alt') || '').toLowerCase();
  const label = `${cls} ${alt} ${src.toLowerCase()}`;
  const width = attrNum(img, 'width');
  const height = attrNum(img, 'height');
  const smallDims = (width !== null && width <= 80) || (height !== null && height <= 80);
  const badgeDims = (width === null || width <= 200) && (height === null || height <= 200);
  const ariaHidden =
    img.getAttribute?.('aria-hidden') === 'true' ||
    img.parentNode?.getAttribute?.('aria-hidden') === 'true';

  // 1. Logo — caller's profile match, or class/ancestor cs-logo, or the
  //    legacy label heuristic.
  if (typeof opts.isLogo === 'function' && opts.isLogo(img, src)) return ASSET_ROLES.LOGO;
  if (cls.includes('cs-logo') || hasAncestorClass(img, 'cs-logo', 2) || LOGO_TOKEN_RE.test(label)) {
    return ASSET_ROLES.LOGO;
  }

  // 2. Explicit decoration classes outrank the generic icon-shape bucket.
  if (cls.includes('cs-floater') || hasAncestorClass(img, 'cs-floater', 2))
    return ASSET_ROLES.BACKGROUND;

  // 3. Icon signals (checked before social/badge so cs-icon always wins the
  //    generic bucket; social/badge refine icon-shaped images).
  const iconShaped =
    cls.includes('cs-icon') ||
    hasAncestorClass(img, 'cs-icon-wrapper', 2) ||
    src.includes('/assets/svgs/') ||
    src.includes('/svgs/') ||
    cls.includes('cs-chevron') ||
    cls.includes('cs-arrow') ||
    (SVG_EXT_RE.test(src) && (smallDims || (width === null && height === null))) ||
    (ariaHidden && SVG_EXT_RE.test(src));

  if (iconShaped && SOCIAL_TOKEN_RE.test(label)) return ASSET_ROLES.SOCIAL_ICON;
  if (iconShaped) return ASSET_ROLES.ICON;

  // 3. Social/badge tokens on non-icon-shaped images.
  if (SOCIAL_TOKEN_RE.test(label) && badgeDims) return ASSET_ROLES.SOCIAL_ICON;
  if (BADGE_TOKEN_RE.test(label) && badgeDims) return ASSET_ROLES.BADGE;

  // 4. Avatar — inside a testimonial/review card that carries a .cs-name, or
  //    explicitly labeled, or a small square.
  const card = closestCard(img);
  if (card?.querySelector?.('.cs-name')) return ASSET_ROLES.AVATAR;
  if (AVATAR_TOKEN_RE.test(label)) return ASSET_ROLES.AVATAR;
  if (
    width !== null &&
    height !== null &&
    width === height &&
    width <= 150 &&
    RASTER_EXT_RE.test(src)
  ) {
    return ASSET_ROLES.AVATAR;
  }

  // 5. Remaining svg art is decorative background.
  if (SVG_EXT_RE.test(src)) return ASSET_ROLES.BACKGROUND;

  // 6. Photo — cs-picture groups, cs-background rasters, raster extensions.
  if (hasAncestorClass(img, 'cs-picture', 2) || cls.includes('cs-picture'))
    return ASSET_ROLES.PHOTO;
  if (RASTER_EXT_RE.test(src)) return ASSET_ROLES.PHOTO;
  if (hasAncestorClass(img, 'cs-background', 2) || cls.includes('cs-background'))
    return ASSET_ROLES.PHOTO;

  // 7. Unknown extension (query-string CDN urls etc.) — photo-ish unless
  //    aria-hidden decoration.
  return ariaHidden ? ASSET_ROLES.ICON : ASSET_ROLES.PHOTO;
}

/** Roles whose slots may receive client photography. */
export function roleAcceptsClientPhoto(role) {
  return role === ASSET_ROLES.PHOTO;
}
